import { FlatTreeControl } from '@angular/cdk/tree';
import { TreeLikeFlatNode } from '@common/data-models/search-results/search-results';
import { NavigationDirection } from '@common/enums/navigation-direction';
import { FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { TITLE_SEPARATOR } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { SidecarStatesMap } from '@src/suite/common/store/fullscreen';
import { Observable, of } from 'rxjs';

import { findIfStoryVisibleInSidecar, fixStoryTitle } from './record-utils';

/**
 * Navigates through the tree nodes in the specified direction (NEXT, PREVIOUS, FIRST, LAST),
 * wrapping around the list when the beginning or end is reached.
 *
 * The function filters nodes by a given tree level (automatically determined from view state if not provided),
 * and returns the first eligible node based on type and visibility rules:
 *
 * - For records of type `SCRIPT`:
 *   - If both `sidecarStates` and `currentTemplate` are provided,
 *     the function checks if the story is already visible in any of the active sidecars.
 *   - If the `SCRIPT` is visible in a sidecar, it is skipped.
 *   - If not visible (or visibility inputs are missing), it is considered eligible and returned.
 *
 * - For all other record types:
 *   - Visibility is not checked.
 *   - These records are always considered eligible and returned.
 *
 * The navigation wraps around the candidate list, ensuring that every node is checked once.
 * If no eligible node is found (e.g. all `SCRIPT`s are visible), the function returns `undefined`.
 */
export const navigateTo = (
    direction: NavigationDirection,
    currentNodeUuid: string,
    candidates: {
        item: TreeLikeFlatNode;
        index: number;
    }[],
    sidecarStates?: SidecarStatesMap,
    currentTemplate?: FullscreenLayoutTemplate
): TreeLikeFlatNode | undefined => {
    const total = candidates.length;
    if (total === 0) return undefined;

    let currentIndex = candidates.findIndex(pair => pair?.item.uniqueId === currentNodeUuid);
    if (currentIndex === -1) {
        currentIndex = candidates.findIndex(pair => {
            const record = pair?.item.object as StorytellingRecord;
            return record?.Guid && currentNodeUuid.includes(record.Guid);
        });
    }

    if (candidates.length === 0) return undefined;

    let candidatePosition = candidates.findIndex(({ index }) => index === currentIndex);

    switch (direction) {
        case NavigationDirection.FIRST:
            candidatePosition = 0;
            break;
        case NavigationDirection.LAST:
            candidatePosition = candidates.length - 1;
            break;
        case NavigationDirection.NEXT:
            candidatePosition = (candidatePosition + 1) % candidates.length;
            break;
        case NavigationDirection.PREVIOUS:
            candidatePosition = (candidatePosition - 1 + candidates.length) % candidates.length;
            break;
    }

    let checked = 0;

    while (checked < candidates.length) {
        const { item } = candidates[candidatePosition];
        const record = item?.object as StorytellingRecord;

        const isScript = record?.Type === ObjectTypes.SCRIPT;
        const shouldCheckVisibility = isScript && sidecarStates && currentTemplate;
        const isVisible = shouldCheckVisibility && findIfStoryVisibleInSidecar(sidecarStates, record.Guid, currentTemplate);

        if (!isVisible) {
            return item;
        }

        if (direction === NavigationDirection.NEXT || direction === NavigationDirection.FIRST) {
            candidatePosition = (candidatePosition + 1) % candidates.length;
        } else {
            candidatePosition = (candidatePosition - 1 + candidates.length) % candidates.length;
        }

        checked++;
    }

    return undefined;
};

export const navigateDirectly = (nodeUniqueId: string, treeControl: FlatTreeControl<TreeLikeFlatNode, string>) => {
    return treeControl.dataNodes.find(item => item?.uniqueId === nodeUniqueId);
};

export const navigateToImage = (navigatation: NavigationDirection, currentMedia: string, images: string[]) => {
    const currentIndex = images?.findIndex(item => item === currentMedia);

    if (navigatation === NavigationDirection.FIRST) {
        return images.find(item => item === images[0]);
    }
    if (navigatation === NavigationDirection.LAST) {
        return images.findLast(item => item === images[images.length - 1]);
    }
    if (navigatation === NavigationDirection.PREVIOUS) {
        const prevItemIdx = images.findLastIndex((item, index) => {
            return index < currentIndex && item === images[index];
        });
        if (prevItemIdx > -1) {
            return images[prevItemIdx];
        } else {
            return navigateToImage(NavigationDirection.LAST, currentMedia, images);
        }
    }
    if (navigatation === NavigationDirection.NEXT) {
        const nextItemIdx = images.findIndex((item, index) => {
            return index > currentIndex && item === images[index];
        });
        if (nextItemIdx > -1) {
            return images[nextItemIdx];
        } else {
            return navigateToImage(NavigationDirection.FIRST, currentMedia, images);
        }
    }
};

/**
 * Navigates through the list of stories in a given direction, skipping any that are
 * already visible in sidecars. If the end or beginning of the list is reached, it wraps around.
 *
 * Returns the first non-visible story found, or undefined if all stories are currently visible.
 */

export const navigateToStory = (
    direction: NavigationDirection,
    currentIndex: number,
    stories: StorytellingStoryRecord[],
    sidecarStates: SidecarStatesMap,
    currentTemplate: FullscreenLayoutTemplate,
    fullscreenRecordGuid: string
): StorytellingStoryRecord | undefined => {
    const total = stories.length;
    if (total === 0) return undefined;

    let index: number;

    switch (direction) {
        case NavigationDirection.FIRST:
            index = 0;
            break;
        case NavigationDirection.NEXT:
            index = (currentIndex + 1) % total;
            break;
        case NavigationDirection.LAST:
            index = total - 1;
            break;
        case NavigationDirection.PREVIOUS:
            index = (currentIndex - 1 + total) % total;
            break;
        default:
            index = currentIndex;
    }

    let checked = 0;

    while (checked < total) {
        const story = stories[index];
        const isVisibleInAnySidecar = findIfStoryVisibleInSidecar(sidecarStates, story.Guid, currentTemplate);
        const isVisibleOnFulscreen = story.Guid === fullscreenRecordGuid;

        if (!isVisibleInAnySidecar && !isVisibleOnFulscreen) {
            return story;
        }

        if (direction === NavigationDirection.NEXT) {
            index = (index + 1) % total;
        } else if (direction === NavigationDirection.PREVIOUS) {
            index = (index - 1 + total) % total;
        } else {
            index = (index + total) % total;
        }

        checked++;
    }

    return undefined;
};

export const extractLowestNodeLevel = (dataNodes: TreeLikeFlatNode[]) => {
    if (dataNodes.length === 0) {
        return null;
    }
    let lvl = -1;
    dataNodes.forEach(node => {
        if (node.level > lvl) {
            lvl = node.level;
        }
    });
    if (lvl === -1) {
        return null;
    }
    return lvl;
};

export const checkForDuplicateSlugObs = (destRundown: StorytellingRundownRecord, story: StorytellingStoryRecord, titleSeparator: string = TITLE_SEPARATOR): Observable<StorytellingStoryRecord> => {
    if (destRundown?.DupeSlugs) {
        return of(null);
    }
    const fixedTitle = fixStoryTitle(story, titleSeparator);
    const duplicateTitleStory = destRundown?.children?.find(s => s?.Guid !== story?.Guid && fixStoryTitle(s, titleSeparator) === fixedTitle);

    return of(duplicateTitleStory);
};
